import { useMemo } from "react";
import {
    isAgentWorking as isRunWorking,
    useAgentRunForClose,
} from "@components/presence/useAgentRunForTask";
import { useOrganizationPresenceUsers } from "@components/presence/usePresenceUsers";
import { useWorkViewerIds } from "@components/presence/WorkViewers";
import { parseCloseId } from "@hooks/useOpenWorkRow";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import { getWorkCardPresenceMembers } from "./workCardPresence";

/** The work's latest agent run, and whether the agent is working on it (the row's flag until the run loads). */
export const useWorkAgentRun = (row: ClientsWorkOverviewWorkRow) => {
    const { run, isLoading } = useAgentRunForClose({
        closeId: parseCloseId(row.closeId) ?? null,
        workspaceId: row.workspaceId,
    });
    return {
        run,
        isAgentWorking: run
            ? isRunWorking(run)
            : isLoading && row.isAgentRunning,
    };
};

/** Who is viewing one of the work's tasks, as the organization's members list them. */
export const useWorkViewers = (row: ClientsWorkOverviewWorkRow) =>
    useOrganizationPresenceUsers(
        useWorkViewerIds({
            closeId: row.closeId,
            workspaceId: row.workspaceId,
        }),
    );

/** A Work card's presence stack members: the agent, live viewers, then the engagement team. */
export const useWorkCardPresence = ({
    row,
    isAgentWorking,
}: {
    row: ClientsWorkOverviewWorkRow;
    isAgentWorking: boolean;
}) => {
    const viewers = useWorkViewers(row);
    return useMemo(
        () => getWorkCardPresenceMembers({ row, isAgentWorking, viewers }),
        [row, isAgentWorking, viewers],
    );
};
